import { useState, useRef } from "react";
import { toast } from "sonner";
import api, { fileToDataUrl, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ImagePlus, Download, RefreshCw, Sparkles } from "lucide-react";
import { ShareBar } from "@/components/ShareBar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BADGES = ["Just Listed", "Open House", "New Price", "Coming Soon", "Sold"];

export default function GraphicsView() {
  const [photo, setPhoto] = useState("");
  const [preview, setPreview] = useState("");
  const [logo, setLogo] = useState("");
  const [headshot, setHeadshot] = useState("");
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState({ badge: "Just Listed", headline: "", subline: "", brand_color: "#D4AF37", agent_name: "", agent_phone: "" });
  const photoRef = useRef(null);
  const logoRef = useRef(null);
  const headshotRef = useRef(null);

  const pick = (setter, withPreview) => async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const dataUrl = await fileToDataUrl(file);
    setter(dataUrl);
    if (withPreview) setPreview(dataUrl);
  };

  const generate = async () => {
    if (!photo) { toast.error("Upload a listing photo first."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/graphics", {
        photo_base64: photo, badge: f.badge, headline: f.headline, subline: f.subline,
        brand_color: f.brand_color, agent_name: f.agent_name, agent_phone: f.agent_phone,
        logo_base64: logo, headshot_base64: headshot,
      });
      setResult(data.image_base64);
      toast.success("Graphic ready!");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  const download = () => {
    const a = document.createElement("a");
    a.href = result; a.download = `${f.badge.replace(/\s+/g, "-").toLowerCase()}-graphic.png`; a.click();
  };

  const publish = async () => {
    const { data } = await api.post("/upload", { image_base64: result, filename: "graphic.png" });
    return `${BACKEND_URL}${data.url}`;
  };

  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4">
        <div>
          <h2 className="font-semibold text-lg">Branded Social Graphic</h2>
          <p className="text-sm text-slate-500 mt-1">Auto-create a polished "Just Listed" / "Open House" post with your branding.</p>
        </div>
        <div>
          <Label>Listing photo</Label>
          <div onClick={() => photoRef.current?.click()} data-testid="graphic-photo-dropzone" className="mt-1.5 border-2 border-dashed border-slate-300 rounded-lg h-44 flex items-center justify-center cursor-pointer hover:border-slate-400 overflow-hidden">
            {preview ? <img src={preview} alt="" className="w-full h-full object-cover" /> : <span className="text-slate-400 flex flex-col items-center"><ImagePlus className="h-6 w-6" /> Upload photo</span>}
          </div>
          <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={pick(setPhoto, true)} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Badge</Label>
            <Select value={f.badge} onValueChange={(v) => setF({ ...f, badge: v })}>
              <SelectTrigger className="mt-1.5" data-testid="graphic-badge"><SelectValue /></SelectTrigger>
              <SelectContent>{BADGES.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Brand color</Label>
            <input type="color" value={f.brand_color} onChange={(e) => setF({ ...f, brand_color: e.target.value })} className="mt-1.5 h-10 w-full rounded border border-slate-200" />
          </div>
        </div>
        <div><Label>Headline</Label><Input data-testid="graphic-headline" value={f.headline} onChange={(e) => setF({ ...f, headline: e.target.value })} className="mt-1.5" placeholder="123 Maple Ave" /></div>
        <div><Label>Subline</Label><Input value={f.subline} onChange={(e) => setF({ ...f, subline: e.target.value })} className="mt-1.5" placeholder="$850,000 · 4 bed · 3 bath" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Agent name</Label><Input value={f.agent_name} onChange={(e) => setF({ ...f, agent_name: e.target.value })} className="mt-1.5" /></div>
          <div><Label>Agent phone</Label><Input value={f.agent_phone} onChange={(e) => setF({ ...f, agent_phone: e.target.value })} className="mt-1.5" /></div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => logoRef.current?.click()}>{logo ? "Logo ✓" : "Upload logo"}</Button>
          <Button variant="outline" size="sm" onClick={() => headshotRef.current?.click()}>{headshot ? "Headshot ✓" : "Upload headshot"}</Button>
          <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={pick(setLogo, false)} />
          <input ref={headshotRef} type="file" accept="image/*" className="hidden" onChange={pick(setHeadshot, false)} />
        </div>
        <Button onClick={generate} disabled={loading} data-testid="graphic-generate-btn" className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-semibold">
          {loading ? <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Designing…</> : <><Sparkles className="h-4 w-4 mr-2" /> Generate graphic</>}
        </Button>
      </div>

      <div>
        {!result ? (
          <div className="bg-white border border-dashed border-slate-300 rounded-lg h-full min-h-[300px] flex items-center justify-center text-slate-400" data-testid="graphic-empty">Your branded post preview appears here.</div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-lg p-5">
            <img src={result} alt="Branded graphic" className="w-full rounded-lg" data-testid="graphic-result" />
            <Button onClick={download} data-testid="graphic-download-btn" className="w-full mt-4 bg-slate-900 hover:bg-slate-800 text-white"><Download className="h-4 w-4 mr-2" /> Download PNG (1080×1080)</Button>
            <ShareBar publish={publish} caption={[f.headline, f.subline].filter(Boolean).join(" — ") || f.badge} downloadName="just-listed-graphic.png" testidPrefix="graphic-share" />
          </div>
        )}
      </div>
    </div>
  );
}
